import {
  DEFAULT_REWRITE_OPTIONS,
  MAX_INSTRUCTION_LENGTH,
  type CustomTone,
  type MenuSetting,
  type RewriteOptions,
  type SavedInstruction,
} from '@lyrmo/shared';
import { useEffect, useEffectEvent, useId, useState } from 'react';
import { Icon } from '../../components/Icon';
import { usePresence } from '../../hooks/usePresence';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { InstructionChips, InstructionMenu } from './InstructionMenu';
import { languageItems, lengthItems } from './menu-items';
import { OptionMenu } from './OptionMenu';
import { ToneMenu } from './ToneMenu';
import { usePinnedLanguages } from './usePinnedLanguages';
import './rewrite-options.css';

// The reset button leaves after the last chip has settled (options-return in the stylesheet).
const RESET_LEAVE_MS = 560;

interface RewriteOptionsBarProps {
  readonly value: RewriteOptions;
  readonly onChange: (value: RewriteOptions) => void;
  /** The writer's own tones, listed after the built-in ones; null until they're known. */
  readonly customTones?: readonly CustomTone[] | null | undefined;
  /** Which tones the writer hid from the tone menu or pinned to its top; null until known. */
  readonly toneSettings?: readonly MenuSetting[] | null | undefined;
  /** Opens Manage tones; omitted where there's no account to keep them. */
  readonly onManageTones?: (() => void) | undefined;
  /** Starts a new tone of the writer's own; omitted where there's no account to keep it. */
  readonly onNewTone?: (() => void) | undefined;
  /** The writer's saved instructions, to choose any of; null until they're known. */
  readonly savedInstructions?: readonly SavedInstruction[] | null | undefined;
  /** Which saved instructions the writer hid from the menu or pinned to its top. */
  readonly instructionSettings?: readonly MenuSetting[] | null | undefined;
  /** Opens Manage instructions; selection remains available without management. */
  readonly onManageInstructions?: (() => void) | undefined;
  /** Starts a new saved instruction: from the one typed for this rewrite, or from nothing. */
  readonly onNewInstruction?: ((text: string) => void) | undefined;
  /** Back to the draft, once done with the instruction menu. */
  readonly onInstructionDone?: (() => void) | undefined;
}

/**
 * The editor's toolbar: tone, from a menu of the built-in tones and the writer's own, length,
 * and language, plus instructions. It sits inside the editor, so the columns still line up.
 */
export function RewriteOptionsBar({
  value,
  onChange,
  customTones = null,
  toneSettings,
  onManageTones,
  onNewTone,
  savedInstructions: providedInstructions,
  instructionSettings,
  onManageInstructions,
  onNewInstruction,
  onInstructionDone,
}: RewriteOptionsBarProps) {
  const { t, format } = useI18n();
  const [pinnedLanguages, togglePinnedLanguage] = usePinnedLanguages();
  const savedInstructions = providedInstructions ?? null;
  const hasInstructionMenu = Boolean(onManageInstructions) || providedInstructions !== undefined;
  const [instructionOpen, setInstructionOpen] = useState(Boolean(value.instruction));
  const instructionId = useId();
  const set = (changes: Partial<RewriteOptions>) => {
    onChange({ ...value, ...changes });
  };

  // A custom tone deleted, or kept from another account, falls back to the built-in tone.
  const toneGone =
    value.customTone !== undefined &&
    customTones !== null &&
    !customTones.some((tone) => tone.id === value.customTone);
  const dropCustomTone = useEffectEvent(() => {
    set({ customTone: undefined });
  });
  useEffect(() => {
    if (toneGone) dropCustomTone();
  }, [toneGone]);

  // So do saved instructions that are gone, or kept from another account.
  const chosen = value.savedInstructions ?? [];
  const kept =
    savedInstructions === null
      ? chosen
      : chosen.filter((id) => savedInstructions.some((instruction) => instruction.id === id));
  const instructionsGone = kept.length < chosen.length;
  const dropInstructions = useEffectEvent(() => {
    set({ savedInstructions: kept.length > 0 ? kept : undefined });
  });
  useEffect(() => {
    if (instructionsGone) dropInstructions();
  }, [instructionsGone]);

  // Anything changed from how the options first were, which Reset puts back.
  const changed =
    value.tone !== DEFAULT_REWRITE_OPTIONS.tone ||
    value.customTone !== undefined ||
    value.length !== DEFAULT_REWRITE_OPTIONS.length ||
    value.language !== DEFAULT_REWRITE_OPTIONS.language ||
    Boolean(value.instruction) ||
    chosen.length > 0;
  const { present: resetPresent, leaving: resetLeaving } = usePresence(changed, RESET_LEAVE_MS);

  return (
    <div
      className={cx('editor-toolbar', resetLeaving && 'is-resetting')}
      role="group"
      aria-label={t.options.group}
    >
      <div className="toolbar-row">
        <div className="toolbar-options">
          <ToneMenu
            // A custom tone that's gone is as good as none, until the effect above drops it.
            value={toneGone ? { tone: value.tone } : value}
            onChange={set}
            customTones={customTones}
            settings={toneSettings}
            onManage={onManageTones}
            onNew={onNewTone}
          />
          <OptionMenu
            label={t.options.length}
            icon="lines"
            items={lengthItems(t)}
            value={value.length}
            onChange={(length) => {
              set({ length });
            }}
          />
          <OptionMenu
            label={t.options.language}
            icon="globe"
            items={languageItems((language) => format.language(language), t, pinnedLanguages)}
            value={value.language}
            heading={t.options.languages}
            onPin={togglePinnedLanguage}
            onChange={(language) => {
              set({ language });
            }}
          />
          {resetPresent && (
            <button
              type="button"
              className={cx('toolbar-button options-reset', resetLeaving && 'is-leaving')}
              // Named in full: narrow, the button is just its icon.
              aria-label={t.options.resetTitle}
              title={t.options.resetTitle}
              aria-hidden={resetLeaving || undefined}
              inert={resetLeaving}
              disabled={resetLeaving}
              onClick={(event) => {
                setInstructionOpen(false);
                onChange({ ...DEFAULT_REWRITE_OPTIONS });
                // Reset leaves the toolbar; keyboard users continue from its first option.
                if (event.detail === 0)
                  event.currentTarget
                    .closest('.editor-toolbar')
                    ?.querySelector<HTMLButtonElement>('.tone-menu > button')
                    ?.focus();
              }}
            >
              <Icon name="retry" />
              <span className="toolbar-button-text">{t.options.reset}</span>
            </button>
          )}
        </div>
        <div className="toolbar-actions">
          {hasInstructionMenu ? (
            <InstructionMenu
              value={value}
              onChange={onChange}
              saved={savedInstructions}
              settings={instructionSettings}
              onManage={onManageInstructions}
              onNew={onNewInstruction}
              onDone={onInstructionDone}
            />
          ) : (
            <button
              type="button"
              className={cx('toolbar-button', value.instruction && 'is-set')}
              aria-label={t.options.instruction}
              title={t.options.instruction}
              aria-expanded={instructionOpen}
              aria-controls={instructionId}
              onClick={() => {
                setInstructionOpen((open) => !open);
              }}
            >
              <Icon name="sparkle" />
              <span className="toolbar-button-text">{t.options.instruction}</span>
            </button>
          )}
        </div>
      </div>
      {hasInstructionMenu && (
        <InstructionChips value={value} onChange={onChange} saved={savedInstructions} />
      )}
      {!hasInstructionMenu && instructionOpen && (
        <div className="toolbar-instruction" id={instructionId}>
          <input
            type="text"
            className="toolbar-instruction-input"
            aria-label={t.options.instructionLabel}
            placeholder={t.options.instructionPlaceholder}
            maxLength={MAX_INSTRUCTION_LENGTH}
            value={value.instruction ?? ''}
            onChange={(event) => {
              set({ instruction: event.target.value || undefined });
            }}
          />
          {value.instruction && (
            <button
              type="button"
              className="button button--ghost icon-button"
              aria-label={t.options.removeInstruction}
              title={t.options.removeInstruction}
              onClick={() => {
                set({ instruction: undefined });
              }}
            >
              <Icon name="close" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
